if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantIdInput?.removeAttribute('disabled');
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButtonText = this.submitButton.querySelector('[data-submit-text]') || this.submitButton.querySelector('span');
        this.buttonPriceWrap = this.submitButton.querySelector('[data-button-price-wrap]');
        this.buttonPriceCurrent = this.submitButton.querySelector('[data-button-price-current]');
        this.buttonPriceCompare = this.submitButton.querySelector('[data-button-price-compare]');
        this.quantityInput = this.form.querySelector('[name="quantity"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.quantityInput?.addEventListener('input', this.updateButtonPrice.bind(this));
        this.quantityInput?.addEventListener('change', this.updateButtonPrice.bind(this));
        this.variantIdInput?.addEventListener('change', this.updateButtonPrice.bind(this));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
        this.updateButtonPrice();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onVariantChange(event) {
        const variant = event?.data?.variant;
        const sectionId = event?.data?.sectionId;
        if (!variant) return;
        if (sectionId && sectionId !== this.dataset.sectionId) return;

        this.dataset.currentVariantPrice = variant.price || 0;
        this.dataset.currentVariantComparePrice = variant.compare_at_price || 0;
        this.updateButtonPrice();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              if (this.buttonPriceWrap) this.buttonPriceWrap.hidden = true;
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const cardQuickAddModal = this.dataset.closeOnSuccess === 'true' ? this.closest('modal-dialog') : null;
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (cardQuickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              cardQuickAddModal.hide();
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            this.handleErrorMessage(window.cartStrings.error, true);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force && errorMessage) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('p');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const message = document.createElement('span');
          message.className = 'product-form__error-message';
          this.errorMessageWrapper.append(message);
          this.form.before(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          if (this.buttonPriceWrap) this.buttonPriceWrap.hidden = true;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            this.submitButtonText.dataset.defaultSubmitText || window.variantStrings.addToCart;
          if (this.buttonPriceWrap && this.dataset.showButtonPrice === 'true') this.buttonPriceWrap.hidden = false;
        }
        this.updateButtonPrice();
      }

      updateButtonPrice() {
        if (!this.buttonPriceWrap || this.dataset.showButtonPrice !== 'true') return;

        const quantity = Math.max(parseInt(this.quantityInput?.value || '1', 10) || 1, 1);
        const price = parseInt(this.dataset.currentVariantPrice || '0', 10) || 0;
        const comparePrice = parseInt(this.dataset.currentVariantComparePrice || '0', 10) || 0;
        const total = price * quantity;
        const compareTotal = comparePrice * quantity;

        this.buttonPriceCurrent.textContent = this.formatButtonMoney(total);

        if (this.buttonPriceCompare && this.dataset.showComparePrice === 'true' && compareTotal > total) {
          this.buttonPriceCompare.textContent = this.formatButtonMoney(compareTotal);
          this.buttonPriceCompare.hidden = false;
        } else if (this.buttonPriceCompare) {
          this.buttonPriceCompare.hidden = true;
          this.buttonPriceCompare.textContent = '';
        }
      }

      formatButtonMoney(cents) {
        const currencyCode = this.dataset.currencyCode || 'USD';
        const roundPrice = this.dataset.roundButtonPrice === 'true';
        const amount = cents / 100;

        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: roundPrice ? 0 : 2,
            maximumFractionDigits: roundPrice ? 0 : 2,
          }).format(amount);
        } catch (error) {
          const roundedAmount = roundPrice ? Math.round(amount).toString() : amount.toFixed(2);
          return `$${roundedAmount}`;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchStandardEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      dispatchStandardEvent(event) {
        if (typeof event?.promise?.catch === 'function') event.promise.catch(() => {});
        this.dispatchEvent(event);
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const cartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => {
              if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
              return response.json();
            });

        cartDataPromise
          .then((cart) => {
            if (!cart?.currency) throw new Error('Missing currency in cart response');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
